import React from 'react';
import PropTypes from 'prop-types';

import {
  TextField,
  Button,
  Box,
  InputLabel,
  Typography,
  FormControlLabel,
  Checkbox,
  Link,
} from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import MaskedInput from 'react-text-mask';

import StepTitle from 'ui_patient/pages/MaskSelectorTool/StepTitle';
import MaskSelectorToolStyles from 'ui_patient/pages/MaskSelectorTool/MaskSelectorToolStyles';

import { useTranslation } from 'react-i18next';
import CheckMark from 'ui_patient/components/BrandedCheckMark';
import {
  shouldShowEUPrivacyAgreement,
  isCookieForPersonalInformationEnabled,
} from 'ui_patient/AppSettings';

import { setCookie } from 'ui_patient/CookieUtil';

const buttonNavigationHeight = '110px';

const REGIONS = {
  US: {
    tncUrl: process.env.REACT_APP_TERMS_AND_CONDITIONS_URL,
    privacyUrl: process.env.REACT_APP_PRIVACY_POLICY_URL,
    consumerHealthUrl: process.env.REACT_APP_CONSUMER_HEALTH_URL,
  },
  AU: {
    tncUrl: process.env.REACT_APP_TERMS_AND_CONDITIONS_URL,
    privacyUrl: process.env.REACT_APP_PRIVACY_POLICY_URL,
    consumerHealthUrl: process.env.REACT_APP_CONSUMER_HEALTH_URL,
  },
  // Add new regions here following the same structure
};

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'block',
    '& .MuiTextField-root': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      width: '100%',
    },
    '@media only screen and (max-width: 639px)': {
      height: `calc(100% - ${buttonNavigationHeight})`,
    },
    paddingTop: '14px',
  },
  labels: {
    color: '#424243',
    fontSize: 14,
    fontWeight: 600,
  },
  customTextField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
      height: '56px',

      '& fieldset': {
        borderWidth: '1px',
        borderColor: '#767676',
      },

      '& input': {
        color: '#212121',
        padding: '8px 12px',
      },

      // Overwrites Mui theme color for focus.
      '&.Mui-focused fieldset': {
        borderColor: '#006DB7',
      },
    },

    '& label.Mui-focused': {
      color: '#006DB7',
    },

    '& .MuiInput-underline:after': {
      borderBottomColor: '#006DB7',
    },

    '& .MuiFilledInput-underline:after': {
      borderBottomColor: '#006DB7',
    },

    // root selector but with higher CSS specificity
    '&&': {
      margin: '6px 0px',
    },
  },
  dobTextField: {
    '@media only screen and (min-width: 640px)': {
      minWidth: '146px',
      maxWidth: '146px',
    },
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'column',
    '@media only screen and (min-width: 640px)': {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: '16px',
    },
  },
  continueButton: {
    '@media only screen and (max-width: 639px)': {
      minWidth: '100%',
      maxWidth: '100%',
    },
  },
  desktopButton: {
    '@media only screen and (max-width: 639px)': {
      display: 'none',
    },
  },
}));

const TextMaskCustom = React.forwardRef((props, ref) => {
  const mask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
  
  return (
    <MaskedInput
      {...props}
      guide={!!props.value}
      mask={mask}
      placeholder={props.placeholder}
    />
  );
});

const PatientInformation = ({
  values,
  errors,
  handleChange,
  handleNext,
  validateForm,
  isValid,
}) => {
  const validateThenNext = async () => {
    const invalidForm = await validateForm();
    if (!Object.keys(invalidForm).length) {
      if (isCookieForPersonalInformationEnabled()) {
        let settings = {
          first_name: values.first_name,
          last_name: values.last_name,
          date_of_birth: values.date_of_birth,
          biometrics_consent_accepted: true,
        };
        let str = JSON.stringify(settings);
        setCookie(str, 20);
      }

      return handleNext();
    }
  };

  const redAsterisk = () => {
    return <span style={{ color: 'red' }}>*</span>;
  };

  const { classes } = MaskSelectorToolStyles();
  const { classes: localClasses } = useStyles();

  const { t } = useTranslation('pages.MaskSelectorTool.PatientInformation');
  const { t: tstart } = useTranslation('pages.MaskSelectorTool.StartPage');

  // Override date format for AU region even when using en_us locale
  const buildRegion = process.env.REACT_APP_BUILD_REGION;
  const getRegionSpecificDatePlaceholder = () => {
    if (buildRegion === 'AU') {
      return 'DD/MM/YYYY'; // AU uses DD/MM/YYYY regardless of locale
    }
    return t('datePlaceholder'); // Use locale-specific placeholder for other regions
  };

  // Localization Strings
  const myInformationTitle = t('myInformationTitle');
  const firstNameLabel = t('firstNameLabel');
  const lastNameLabel = t('lastNameLabel');
  const birthdayLabel = t('birthdayLabel');
  const emailLabel = 'Email';
  const dateOfBirthLabel = 'Date of birth';
  const continueButton = t('continueButton');
  const datePlaceholder = getRegionSpecificDatePlaceholder();

  const privacyAgreement_AMR = tstart('privacyAgreement');
  const privacyAgreement_AMR2 = tstart('privacyAgreement2');
  const privacyAgreement_AMR3 = tstart('privacyAgreement3');
  const privacyAgreementLink = tstart('privacyAgreementLink');
  const consumerHealthLink = tstart('consumerHealthLink');
  const privacyAgreement_EU = tstart('privacyAgreementEU');

  return (
    <>
      <Box
        sx={{
          marginTop: '24px',
          paddingTop: '24px',
          marginBottom: '24px',
          paddingX:'24px',
          minHeight: 'calc(100svh - 150px)',
        }}
        bgcolor="background.paper"
        className={classes.appRootPadding}
      >
        <StepTitle number="1" title={myInformationTitle} />
        <form className={localClasses.root}>
          <Box className={localClasses.nameContainer}>
            <Box sx={{ marginBottom: 2, flex: '1' }}>
              <InputLabel className={localClasses.labels}>
                {firstNameLabel}
                {redAsterisk()}
              </InputLabel>
              <TextField
                id="first_name"
                value={values.first_name}
                error={!!errors.first_name}
                helperText={errors.first_name}
                onChange={handleChange}
                variant="outlined"
                color="secondary"
                className={localClasses.customTextField}
                data-testid={firstNameLabel}
              />
            </Box>
            <Box sx={{ marginBottom: 2, flex: '1' }}>
              <InputLabel className={localClasses.labels}>
                {lastNameLabel}
                {redAsterisk()}
              </InputLabel>
              <TextField
                id="last_name"
                value={values.last_name}
                error={!!errors.last_name}
                helperText={errors.last_name}
                onChange={handleChange}
                variant="outlined"
                color="secondary"
                className={localClasses.customTextField}
                data-testid={lastNameLabel}
              />
            </Box>
          </Box>

          <Box className={localClasses.nameContainer}>
            <Box sx={{ marginBottom: 2, flex: '1' }}>
              <InputLabel className={localClasses.labels}>
                {emailLabel}
                {redAsterisk()}
              </InputLabel>
              <TextField
                id="email"
                value={values.email}
                error={!!errors.email}
                helperText={errors.email}
                onChange={handleChange}
                variant="outlined"
                color="secondary"
                className={localClasses.customTextField}
                inputProps={{
                  type: 'email',
                }}
                data-testid={emailLabel}
              />
            </Box>
            <Box sx={{ marginBottom: 2, flex: '1' }}>
              <InputLabel className={localClasses.labels}>
                {dateOfBirthLabel}
                {redAsterisk()}
              </InputLabel>
              <TextField
                type="tel"
                id="date_of_birth"
                className={`${localClasses.customTextField} ${localClasses.dobTextField}`}
                value={values.date_of_birth}
                error={!!errors.date_of_birth}
                helperText={errors.date_of_birth}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputComponent: TextMaskCustom,
                  inputProps: {
                    placeholder: datePlaceholder,
                  },
                }}
                variant="outlined"
                color="secondary"
                data-testid={birthdayLabel}
              />
            </Box>
          </Box>

          <Box mt={'24px'}>
            <FormControlLabel
              sx={{ alignItems: 'start' }}
              control={
                <Checkbox
                  name="biometrics_consent_accepted"
                  checked={Boolean(values.biometrics_consent_accepted)}
                  checkedIcon={<CheckMark />}
                  onChange={handleChange}
                  color="default"
                  data-testid="patient-info-checkbox"
                />
              }
              label={
                <Box fontSize={12} lineHeight={1.2}>
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 400 }}
                    data-testid="start-page-privacy-agreement"
                  >
                    {shouldShowEUPrivacyAgreement()
                      ? privacyAgreement_EU
                      : privacyAgreement_AMR}{' '}
                    <Link
                      sx={{
                        fontSize: '14px !important',
                        fontWeight: '400 !important',
                      }}
                      className={classes.res_captionLink}
                      href={REGIONS[process.env.REACT_APP_BUILD_REGION].tncUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      data-testid="start-page-privacy-agreement-link"
                    >
                      Terms & Conditions
                    </Link>
                    ,{'  '}
                    <Link
                      sx={{
                        fontSize: '14px !important',
                        fontWeight: '400 !important',
                      }}
                      className={classes.res_captionLink}
                      href={
                        REGIONS[process.env.REACT_APP_BUILD_REGION].privacyUrl
                      }
                      target="_blank"
                      color="inherit"
                      data-testid="start-page-privacy-agreement-link"
                    >
                      {privacyAgreementLink}
                    </Link>
                    {privacyAgreement_AMR2}
                    <Link
                      sx={{
                        fontSize: '14px !important',
                        fontWeight: '400 !important',
                      }}
                      className={classes.res_captionLink}
                      href={
                        REGIONS[process.env.REACT_APP_BUILD_REGION]
                          .consumerHealthUrl
                      }
                      target="_blank"
                      color="inherit"
                      data-testid="start-page-privacy-agreement-link"
                    >
                      {consumerHealthLink}
                    </Link>
                    {privacyAgreement_AMR3}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </form>

        {/* This button is only shown when in desktop mode. */}
        <Button
          disableRipple
          variant="contained"
          onClick={validateThenNext}
          disabled={!isValid}
          className={`${classes.single_button} ${localClasses.desktopButton}`}
          data-testid="continue-button-desktop"
        >
          {continueButton}
        </Button>
      </Box>

      {/* This button is only shown in mobile mode. */}
      <Box className={classes.patient_continue_sticky}>
        <Box className={classes.patient_continue_sticky_inner}>
          <Button
            disableRipple
            variant="contained"
            onClick={validateThenNext}
            disabled={!isValid}
            className={classes.patient_mobile_single_button}
            data-testid="continue-button-mobile"
            sx={{ height: '40px' }}
          >
            {continueButton}
          </Button>
        </Box>
      </Box>
    </>
  );
};
PatientInformation.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  validateForm: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
};

export default PatientInformation;
